<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";
import { getSpecieList } from "@carnet/api/backoffice";
import type { GetSpecieListData } from "@carnet/api/backoffice";
import type { UrlFilterValues } from "@/composables/useUrlFilters";
import { SPECIES_SORT_VALUES, useSpeciesSortOptions, type SpeciesSort } from "@/composables/useSpeciesSortOptions";

definePageMeta({
  name: "species",
  layout: "app",
});

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: "/especes",
  },
});

const { t } = useI18n();
const localePath = useLocalePath();

const page = useUrlPage();

const { term: search, applied: appliedSearch } = useUrlSearchTerm("q");
const MIN_SEARCH_LENGTH = 3;
const effectiveSearch = computed(() => (appliedSearch.value.length >= MIN_SEARCH_LENGTH ? appliedSearch.value : ""));

const { multi } = useUrlFilterValues();
const taxonIds = multi("taxons");
const featureCodes = multi("specieFeatures");
const zoneCodes = multi("zones");
const { requestLevelCodes } = useSpecieLevelFilter();

const sortOptions = useSpeciesSortOptions();
const sort = useUrlSort<SpeciesSort>("sort", "date_desc", SPECIES_SORT_VALUES);

const filterCount = computed(() => [taxonIds, featureCodes, zoneCodes].filter((r) => r.value.length).length);

const { title, breadcrumbLabel } = usePageTitle({
  base: () => t("species.title"),
  page,
  search: effectiveSearch,
  filterCount,
});

useSeoMeta({
  title: () => title.value,
  description: () => t("seo.species.description"),
});

const breadcrumb = computed<BreadcrumbItem[]>(() => [
  { label: t("home.title"), to: localePath({ name: "index" }) },
  { label: breadcrumbLabel.value },
]);

const filterValues = ref<UrlFilterValues>({});

const {
  data: species,
  status,
  error,
  refresh,
} = await getSpecieList({
  composable: "useLazyFetch",
  key: "species",
  path: { locale: appLocale() },
  query: computed(
    () =>
      ({
        page: page.value,
        size: 18,
        "taxonIds[]": taxonIds.value.length ? taxonIds.value.map(Number) : undefined,
        "featureCodes[]": featureCodes.value.length ? featureCodes.value : undefined,
        "specieLevelCodes[]": requestLevelCodes.value,
        "zoneCodes[]": zoneCodes.value.length ? zoneCodes.value : undefined,
        terms: effectiveSearch.value || undefined,
        sort: sort.value,
      }) satisfies NonNullable<GetSpecieListData["query"]>
  ),
});

const pagination = computed(() => species.value?.data?.pagination);
const speciesList = computed(() => species.value?.data?.list ?? []);
const loading = useIsFetching(status);

watch(
  [taxonIds, featureCodes, requestLevelCodes, zoneCodes, effectiveSearch, sort],
  () => {
    page.value = 1;
  },
  { deep: true }
);

watch(page, () => refresh());
</script>

<template>
  <div class="flex flex-col grow">
    <AppSection :ui="{ base: 'pt-8 pb-4' }">
      <WebBreadcrumbs :items="breadcrumb" class="mb-10" />

      <AppInputSearch
        v-model="search"
        :placeholder="t('species.search')"
        :aria-label="t('species.search')"
        :loading="loading"
        class="mb-4"
        highlight
      />

      <WebFiltersSpecies v-model:applied="filterValues" />
    </AppSection>

    <AppSection class="grow" :ui="{ base: 'pb-24 pt-6' }">
      <WebTabsRow class="pb-4">
        <template #left>
          <AppResultCount :loading="loading">
            {{ t("species.count", pagination?.total ?? speciesList.length) }}
          </AppResultCount>
        </template>
        <template #right>
          <AppSortSelect v-model="sort" :options="sortOptions" />
        </template>
      </WebTabsRow>

      <WebPaginatedList
        v-model:page="page"
        :items="speciesList"
        :pagination="pagination"
        :loading="loading"
        :error="error"
        :skeleton-count="18"
        list-class="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14"
        @refresh="refresh()"
      >
        <template #skeleton><AppTileSpecieSkeleton /></template>
        <template #default="{ item }">
          <AppTileSpecie :specie="item" :to="$localePath({ name: 'species.detail', params: { id: item.id } })" />
        </template>
      </WebPaginatedList>
    </AppSection>
  </div>
</template>
